import { create } from 'zustand';
import type { User, Session } from '@supabase/supabase-js';
import type { UserRole } from '@/types/auth.types';

interface UserProfile {
  id: string;
  email: string;
  display_name?: string;
  role: UserRole;
  restaurant_id: string | null;
  is_active: boolean;
  onboarding_completed?: boolean;
  restaurant_type?: string;
}

interface Restaurant {
  id: string;
  nom: string;
  adresse: string | null;
  telephone: string | null;
  email: string | null;
  owner_id: string;
  plan: string | null;
}

interface AuthState {
  // État
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Multi-restaurants
  restaurants: Restaurant[];
  currentRestaurant: Restaurant | null;

  // Actions
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setProfile: (profile: UserProfile | null) => void;
  setLoading: (isLoading: boolean) => void;
  login: (user: User, session: Session, profile: UserProfile) => void;
  logout: () => void;
  updateProfile: (updates: Partial<UserProfile>) => void;

  // Actions multi-restaurants
  setRestaurants: (restaurants: Restaurant[]) => void;
  setCurrentRestaurant: (restaurant: Restaurant | null) => void;
  switchRestaurant: (restaurantId: string) => void;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  // État initial - toujours en loading au démarrage
  // La session sera vérifiée par authService.initialize()
  user: null,
  session: null,
  profile: null,
  isLoading: true,
  isAuthenticated: false,

  // Multi-restaurants
  restaurants: [],
  currentRestaurant: null,

  // Actions
  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
    }),

  setSession: (session) =>
    set({
      session,
      isAuthenticated: !!session,
    }),

  setProfile: (profile) =>
    set({
      profile,
    }),

  setLoading: (isLoading) =>
    set({
      isLoading,
    }),

  login: (user, session, profile) => {
    set({
      user,
      session,
      profile,
      isAuthenticated: true,
      isLoading: false,
    });
  },

  logout: () =>
    set({
      user: null,
      session: null,
      profile: null,
      isAuthenticated: false,
      isLoading: false,
      restaurants: [],
      currentRestaurant: null,
    }),

  updateProfile: (updates) =>
    set((state) => ({
      profile: state.profile ? { ...state.profile, ...updates } : null,
    })),

  // Actions multi-restaurants
  setRestaurants: (restaurants) =>
    set({ restaurants }),

  setCurrentRestaurant: (restaurant) =>
    set({ currentRestaurant: restaurant }),

  switchRestaurant: (restaurantId) => {
    const { restaurants, profile } = get();
    const restaurant = restaurants.find((r) => r.id === restaurantId);

    if (restaurant) {
      set({
        currentRestaurant: restaurant,
        profile: profile ? { ...profile, restaurant_id: restaurantId } : null,
      });

      // Sauvegarder en localStorage
      try {
        localStorage.setItem('selected_restaurant_id', restaurantId);
      } catch {
        // Ignorer les erreurs localStorage
      }
    }
  },
}));
